/**
 * +polish: keep world-anchored text (combat numbers, reward pops) off the HUD panels. The panel
 * rects (quest tracker, target frame, unit frames, the skill cluster) are re-measured a few times a
 * second; `avoidPanels` pushes a label's centre out of any rect it overlaps, along the shortest way.
 */

/** Panels that floating text must not cover. */
const PANEL_SELECTORS = ['.hk-r-right > *', '.hk-r-target > *', '.hk-r-topLeft > *', '.hk-r-topRight > *', '.hkc-body', '.hkc-main', '.hkc-xp', '.fx-xp', '.hk-r-bottomRight > *'];
const REFRESH_MS = 250;
const PAD = 6;

export interface PanelRect {
  l: number;
  t: number;
  r: number;
  b: number;
}

let rects: PanelRect[] = [];
let measuredAt = -Infinity;

function measure(now: number): void {
  if (now - measuredAt < REFRESH_MS || typeof document === 'undefined') return;
  measuredAt = now;
  rects = [];
  for (const el of document.querySelectorAll<HTMLElement>(PANEL_SELECTORS.join(','))) {
    const r = el.getBoundingClientRect();
    if (r.width < 4 || r.height < 4 || el.offsetParent === null) continue;
    rects.push({ l: r.left - PAD, t: r.top - PAD, r: r.right + PAD, b: r.bottom + PAD });
  }
}

/**
 * Move the point (`out.x`, `out.y`), the centre of a label `w`×`h` px, out of every HUD panel.
 * Pure given `panels` (exported for tests); the DOM entry point is `avoidPanels`.
 */
export function pushOut(out: { x: number; y: number }, w: number, h: number, panels: readonly PanelRect[]): void {
  const hw = w / 2;
  const hh = h / 2;
  for (let pass = 0; pass < 2; pass++) {
    let moved = false;
    for (const p of panels) {
      if (out.x + hw <= p.l || out.x - hw >= p.r || out.y + hh <= p.t || out.y - hh >= p.b) continue;
      const dl = out.x + hw - p.l;
      const dr = p.r - (out.x - hw);
      const dt = out.y + hh - p.t;
      const db = p.b - (out.y - hh);
      const m = Math.min(dl, dr, dt, db);
      if (m === dl) out.x -= dl;
      else if (m === dr) out.x += dr;
      else if (m === dt) out.y -= dt;
      else out.y += db;
      moved = true;
    }
    if (!moved) return;
  }
}

export function avoidPanels(out: { x: number; y: number }, w: number, h: number, now: number): void {
  measure(now);
  if (rects.length) pushOut(out, w, h, rects);
}
